import React from 'react';
import { Icon } from '../Icon/Icon';
import { IconName } from '../Icon/Icon.type';

/** Meaning, not colour — so the palette can change without the call sites lying. */
export type BadgeTone = 'neutral' | 'brand' | 'success' | 'warning' | 'error';

const TONE: Record<BadgeTone, string> = {
  neutral: 'bg-utility-gray-50 border-utility-gray-200 text-utility-gray-700',
  // -900 where its siblings use -700: the brand ramp repeats the same
  // chartreuse at 600 and 700, which leaves the label at 1.6:1 on its own
  // fill. The dark end of the ramp is the only one that stays legible.
  brand: 'bg-utility-brand-50 border-utility-brand-200 text-utility-brand-900',
  success: 'bg-utility-success-50 border-utility-success-200 text-utility-success-700',
  warning: 'bg-utility-warning-50 border-utility-warning-200 text-utility-warning-700',
  error: 'bg-bg-error-primary border-border-error-subtle text-text-error-primary',
};

export interface BadgeProps {
  tone?: BadgeTone;
  /** Shows a spinner ahead of the label, for a state that is still resolving. */
  spinning?: boolean;
  className?: string;
  children: React.ReactNode;
}

/**
 * The pill shape used for statuses, types and counts.
 *
 * Owns the geometry — padding, radius, type scale, no-wrap and the spinner slot
 * — so a pill added anywhere else matches the ones already on screen instead of
 * re-deriving the same six classes.
 */
export const Badge: React.FC<BadgeProps> = ({
  tone = 'neutral',
  spinning = false,
  className = '',
  children,
}) => (
  <span
    className={`${TONE[tone]} border px-bg-v2-md py-bg-v2-xxs rounded-bg-v2-full text-bg-v2-xs font-bg-v2-medium tracking-wide whitespace-nowrap inline-flex items-center gap-bg-v2-xs ${className}`}
  >
    {spinning && <Icon name={IconName.Loading} size="xs" spin inheritColor />}
    {children}
  </span>
);

export default Badge;
